import React, { useState, useEffect } from "react";
import styled from "styled-components";
import PropTypes from "prop-types";
import LegacyIcon from "../icons/legacy-icon";

const CollapsibleWrapper = styled.div`
`;

const CollapsibleContent = styled.section`
  padding: ${(props) => (props.isAdmin ? "0" : "0 1.25rem")};
  height: 0;
  max-height: 0;
  overflow: hidden;
  transition: all 0.3s ease-out;

  &.open {
    padding: ${(props) =>
    props.isAdmin ? "1rem 0 .2rem 0" : "1.75rem 1.25rem"};
    height: auto;
    max-height: unset;
  }
`;

const Header = styled.button`
  border: none;
  width: ${({ width }) => width};
  cursor: pointer;
  height: 2.75rem;
  border-radius: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${(props) => (props.isAdmin ? "0.3125rem" : "0.75rem 1.25rem")};
  background-color: ${(props) => props.theme.themeColors[props.background]};

  :focus {
    outline: 0;
  }

  :focus-visible {
    outline: 0.125rem solid #0070f3;
  }

  svg {
    transition: transform 0.2s ease-out;
  }

  &.open {
    border-radius: 0.75rem;
  }

  &.open svg {
    transform: rotate(180deg);
  }

  :hover {
    background-color: ${({ theme, hoverColor }) =>
    theme.themeColors[hoverColor]};
  }
`;

const Title = styled.h4`
  margin: 0;
  line-height: 1.375rem;
  font-size: ${({ theme }) => theme.fontSize(2)};
  font-weight: 600;
  height: 1.125rem;
  color: ${({ theme }) => theme.themeColors.darkGreen1};
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  text-overflow: ellipsis;
  overflow: hidden;
  text-align: left;
`;

function Collapsible({
  title,
  headerBackground,
  hoverColor,
  children,
  width,
  uniqueId,
  isAdmin,
  startOpen,
  startOpenOnTablet,
  startOpenOnMobile
}) {
  const [open, setOpen] = useState(false);
  // set collapsible to open by default on desktop and closed on
  // tablets and mobile.
  const updateDefaultState = () => {
    const viewportWidth = window.innerWidth;
    if (viewportWidth > 1024) {
      setOpen(startOpen);
    } else if (viewportWidth > 768) {
      setOpen(startOpenOnTablet);
    } else {
      setOpen(startOpenOnMobile);
    }
  };

  useEffect(() => {
    updateDefaultState();
  }, []);

  return (
    <CollapsibleWrapper>
      <Header
        onClick={() => setOpen(!open)}
        className={open ? "open" : ""}
        id={`${uniqueId}-header`}
        aria-controls={`${uniqueId}-panel`}
        aria-expanded={open}
        background={headerBackground}
        width={width}
        isAdmin={isAdmin}
        hoverColor={hoverColor}
      >
        <Title>{title}</Title>
        <LegacyIcon name="chevron-20" color="#192617" />
      </Header>
      <CollapsibleContent
        className={open ? "open" : ""}
        id={`${uniqueId}-panel`}
        aria-labelledby={`${uniqueId}-header`}
        isAdmin={isAdmin}
      >
        {children}
      </CollapsibleContent>
    </CollapsibleWrapper>
  );
}

export default Collapsible;

Collapsible.propTypes = {
  title: PropTypes.string,
  startOpen: PropTypes.bool,
  startOpenOnTablet: PropTypes.bool,
  startOpenOnMobile: PropTypes.bool,
  children: PropTypes.node.isRequired,
  headerBackground: PropTypes.oneOf(["lightGreen4", "blue3", "white"]),
  width: PropTypes.string,
  hoverColor: PropTypes.oneOf(["lightGreen4", "blue3", "white", "blue1"]),
  uniqueId: PropTypes.string.isRequired,
  isAdmin: PropTypes.bool,
};

Collapsible.defaultProps = {
  title: "",
  headerBackground: "lightGreen4",
  width: "100%",
  hoverColor: "white",
  isAdmin: false,
  startOpen: false,
  startOpenOnTablet: false,
  startOpenOnMobile: false
};
